<script>
   /*************************************************************/
   /* ModelLoader                                               */
   /* -------------------------                                 */
   /* loads PCA model from file                                 */
   /*************************************************************/

   import { FileSelector, ButtonCancel, Container, Button } from 'svelte-controls-basic';
   import { reviver, modelsFit } from '@mdatools-apps/shared/utils';
   import { checkPrepField } from '@mdatools-apps/shared/methods/prep.js';
   import PrepInfo from '@mdatools-apps/shared/components/PrepInfo.svelte';

   let { model = $bindable(), simca = $bindable() } = $props();    // object with model


   // string with model file name
   let modelFile = $state('');

   // app status for handling and showing errors
   let status = $state('neutral');
   let errMsg = $state('');

   // gets model content as one large string, parse it into matrix
   function setModel(strData) {
      try{
         const m = JSON.parse(strData, reviver);

         // every step here has to hold before the next one is allowed to look inside. reaching
         // 'm.pca.class[0]' while 'm.pca' was a number or missing raised a raw TypeError, and the
         // user was shown its text instead of a sentence about the file. 'simca' has to be an
         // object too: a null one passed the old presence check, and the settings component then
         // built a replacement with no target class in it
         const isObject = v => v !== null && typeof v === 'object';

         if (!isObject(m) || !isObject(m.pca) || !isObject(m.simca) ||
             !Array.isArray(m.pca.class) || m.pca.class[0] !== 'pcamodel') {
            throw Error('The JSON file you selected does not contain SIMCA model.');
         }

         // check if the object version is from previous app version (no hash field)
         if (!Object.keys(m.pca).includes('hash')) {
            throw Error('This model was created with an older version of the app and can not be loaded. Please create a new model.');
         }

         // the two halves of the file are stored side by side and nothing ties them together, so a
         // file assembled or edited by hand can select more components than the pca model actually
         // has. the settings range clamps what the user picks but never rewrites the value it was
         // handed on load, so the excess reached 'eCrit'/'oCrit' as an out-of-bounds index and put
         // 'undefined' in the results export
         if (!Number.isInteger(m.pca.ncomp) || !Number.isInteger(m.simca.ncomp) ||
             m.simca.ncomp < 1 || m.simca.ncomp > m.pca.ncomp) {
            throw Error('The model file is damaged: it selects ' + m.simca.ncomp + ' components ' +
               'while the PCA model it contains has only ' + m.pca.ncomp + '.');
         }

         // the preprocessing is rendered below, where no catch reaches, and applied by
         // 'app_pca_predict': the checks of a preprocessing model, none for 0 or an empty object
         try { checkPrepField(m.pca.prep); } catch {
            throw Error('The model file is damaged: its preprocessing can not be read.');
         }

         if (Array.isArray(m.pca.v)) {
            m.pca.v = Float64Array.from(m.pca.v);
         }
         // the model is read by offset from its sizes and kept in the session, which a model
         // whose values do not fit them would cost on its next restore
         if (!modelsFit(m)) throw Error('The model file is damaged: its values do not fit its sizes.');

         m.pca['fileName'] = modelFileName;
         model = m.pca;
         simca = m.simca;
         status = 'success';
         errMsg = ''
      } catch (e) {
         model = null;
         status = 'error';
         // a file the parser refuses is named as such, in place of the parser's own text
         errMsg = e instanceof SyntaxError ? 'The file could not be read as JSON.'
            : e instanceof Error ? e.message : String(e);
      }
   }

   // promise for reading the JSON file
   function readFile () {
      const reader = new FileReader();
      reader.onload = () => { setModel(reader.result) };

      // without this a file which can not be read at all - moved or made unreadable between being
      // picked and the button being pressed - left the component in its neutral state, so the
      // button appeared to do nothing
      reader.onerror = () => {
         model = null;
         status = 'error';
         errMsg = 'Could not read the file: ' + (reader.error ? reader.error.message : 'unknown error');
      };

      reader.readAsText(modelFile);
   }

   function reset() {
      modelFile = '';
      model = null;
      status = 'neutral';
      errMsg = '';
   }

   // reactive expression for tracking changes of 'modelFile'
   $effect(() => {
      if (modelFile === null) {
         // if model file is empty reset status and error messages
         model = null;
         status = 'neutral';
         errMsg = '';
      } else {
         // if not just remove error messages if any
         status = 'neutral';
         errMsg = '';
      }
   });

   // file name with model without extension
   let modelFileName =$derived(modelFile ? modelFile.name.replace(/\.[^/.]+$/, "") : '');
</script>

{#if !model }

   <!-- show if data is not loaded -->
   <Container>
      <FileSelector bind:file={modelFile} acceptType=".json" message="Select file with model"/>
   </Container>

   {#if modelFile}
   <Container label="&nbsp;">
      <Button onclick={readFile} text="Load" />
   </Container>
   {/if}
{:else}

   <!-- dataset is loaded - show info and cancel button -->
   <Container name="info">
      <p class="model-info">
         <span>Model file:</span> <span title={model.fileName} class="file-name">{model.fileName}</span><br>
         <span style="color:#a0a0a0;">PCA model:</span> {model.ncomp} PCs{model.center ? ', centered' : ''}{model.scale ? ', scaled' : ''}
         {#if model.prep}
         <br><span style="color:#a0a0a0;">Preprocessing: </span>
            <PrepInfo prepmodel={model.prep} />
         {/if}
      </p>
      <ButtonCancel onclick={reset} />
   </Container>

{/if}

<!-- error message -->
{#if status === 'error' || errMsg}
<Container status="error">
   <p>{errMsg}</p>
</Container>
{/if}


<style>

   .model-info {
      line-height: 1.5em;
   }

   /* not ':global' - the preprocessing summary renders its own span as a direct child of this
      paragraph, and a global selector reached into that component and greyed it out */
   .model-info > span {
      vertical-align:top;
      color: #a0a0a0;
   }

   .model-info > .file-name {
      display: inline-block;
      padding: 0;
      margin: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 25ch;
      color: #222;
   }

</style>